import * as React from "react";
import {cn} from "../lib/fns";

type RadioValue = string | number | readonly string[];

type RadioGroupContextValue = {
  disabled?: boolean;
  name?: string;
  value?: RadioValue;
  onValueChange?: (value: RadioValue, event: React.ChangeEvent<HTMLInputElement>) => void;
};

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(null);

export type RadioChangeEvent = {
  target: {
    value: RadioValue;
    checked: boolean;
    name?: string;
  };
};

export type RadioProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> & {
  rootClassName?: string;
  children?: React.ReactNode;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export type RadioGroupProps = Omit<React.HTMLAttributes<HTMLDivElement>, "defaultValue" | "onChange"> & {
  value?: RadioValue;
  defaultValue?: RadioValue;
  onChange?: (event: RadioChangeEvent) => void;
  disabled?: boolean;
  name?: string;
  children?: React.ReactNode;
};

function RadioBase({
  className,
  rootClassName,
  children,
  value,
  checked,
  disabled,
  name,
  onChange,
  ...props
}: RadioProps) {
  const group = React.useContext(RadioGroupContext);
  const isGrouped = group && value !== undefined;
  const resolvedDisabled = disabled ?? group?.disabled ?? false;
  const resolvedName = name ?? group?.name;
  const resolvedChecked = isGrouped ? group.value === value : checked;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isGrouped && value !== undefined) {
      group.onValueChange?.(value, event);
    }

    onChange?.(event);
  };

  return (
    <label className={cn("inline-flex items-center gap-2", resolvedDisabled && "opacity-50", rootClassName)}>
      <span className={"relative inline-flex h-4 w-4 items-center justify-center"}>
        <input
          {...props}
          type="radio"
          name={resolvedName}
          value={value}
          checked={resolvedChecked}
          disabled={resolvedDisabled}
          onChange={handleChange}
          className={"peer sr-only"}
        />
        <span className={"h-4 w-4 rounded-full border border-neutral-300 bg-white transition-colors peer-checked:border-primary"} />
        <span className={"pointer-events-none absolute h-2 w-2 rounded-full bg-primary opacity-0 transition-opacity peer-checked:opacity-100"} />
      </span>
      {children ? <span className={cn("whitespace-nowrap text-sm", className)}>{children}</span> : null}
    </label>
  );
}

function RadioGroup({
  value,
  defaultValue,
  onChange,
  disabled,
  name,
  className,
  children,
  ...props
}: RadioGroupProps) {
  const [internalValue, setInternalValue] = React.useState<RadioValue | undefined>(defaultValue);
  const selectedValue = value ?? internalValue;

  const handleValueChange = React.useCallback((nextValue: RadioValue, event: React.ChangeEvent<HTMLInputElement>) => {
    if (value === undefined) {
      setInternalValue(nextValue);
    }

    onChange?.({
      target: {
        value: nextValue,
        checked: event.target.checked,
        name: event.target.name,
      },
    });
  }, [onChange, value]);

  const contextValue = React.useMemo(() => ({
    disabled,
    name,
    value: selectedValue,
    onValueChange: handleValueChange,
  }), [disabled, handleValueChange, name, selectedValue]);

  return (
    <RadioGroupContext.Provider value={contextValue}>
      <div className={className} {...props}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

type RadioComponent = React.FC<RadioProps> & {
  Group: React.FC<RadioGroupProps>;
};

export const Radio = RadioBase as RadioComponent;
Radio.Group = RadioGroup;
